import {
  calculateTaxOnIncome,
  calculateMarginalRate,
  calculateEffectiveTaxRate,
  STANDARD_DEDUCTIONS,
  FICA_CONSTANTS,
  STATE_TAX_DATA,
} from '../calculations'

// 2025 QBI thresholds (IRS Revenue Procedure 2024-40)
const QBI_THRESHOLDS = {
  single: { start: 197300, phaseOut: 50000 },
  hoh: { start: 197300, phaseOut: 50000 },
  mfj: { start: 394600, phaseOut: 100000 },
}

interface SelfEmploymentTaxInput {
  grossIncome: number
  businessExpenses: number
  filingStatus: string
  otherW2Income: number
  stateCode: string
  customStateTaxRate?: number
  isSSTB?: boolean
  w2WagesPaidByBiz?: number
  ubiaQualifiedProperty?: number
}

function calculateQBI(
  qbi: number,
  taxableIncomeBeforeQBI: number,
  filingStatus: string,
  isSSTB: boolean,
  w2Wages: number,
  ubia: number,
): { deduction: number; limited: boolean } {
  if (qbi <= 0 || taxableIncomeBeforeQBI <= 0) {
    return { deduction: 0, limited: false }
  }

  const thresholds = QBI_THRESHOLDS[filingStatus] || QBI_THRESHOLDS.single
  const thresholdStart = thresholds.start
  const thresholdEnd = thresholdStart + thresholds.phaseOut

  // Overall cap: QBI deduction can never exceed 20% of taxable income before QBI
  const taxableIncomeCap = taxableIncomeBeforeQBI * 0.20
  const twentyPercentQBI = qbi * 0.20

  // Below threshold: full 20% deduction
  if (taxableIncomeBeforeQBI <= thresholdStart) {
    const deduction = Math.min(twentyPercentQBI, taxableIncomeCap)
    return { deduction, limited: deduction < twentyPercentQBI }
  }

  // Above phase-out range
  if (taxableIncomeBeforeQBI >= thresholdEnd) {
    if (isSSTB) {
      // SSTB above phase-out: no QBI deduction
      return { deduction: 0, limited: true }
    }
    // Non-SSTB above phase-out: W-2/UBIA limit applies fully
    const w2UbiaLimit = Math.max(w2Wages * 0.50, w2Wages * 0.25 + ubia * 0.025)
    const deduction = Math.min(twentyPercentQBI, w2UbiaLimit, taxableIncomeCap)
    return { deduction, limited: true }
  }

  // In phase-out range
  const phaseOutFraction = (taxableIncomeBeforeQBI - thresholdStart) / thresholds.phaseOut

  if (isSSTB) {
    // SSTB: reduce QBI itself during phase-out
    const reducedQBI = qbi * (1 - phaseOutFraction)
    const reducedW2 = w2Wages * (1 - phaseOutFraction)
    const reducedUBIA = ubia * (1 - phaseOutFraction)
    const twentyPctReduced = reducedQBI * 0.20
    const w2UbiaLimit = Math.max(reducedW2 * 0.50, reducedW2 * 0.25 + reducedUBIA * 0.025)
    // Phase in the W-2/UBIA limit
    const excessOverLimit = Math.max(0, twentyPctReduced - w2UbiaLimit)
    const reductionAmount = excessOverLimit * phaseOutFraction
    const deduction = Math.min(twentyPctReduced - reductionAmount, taxableIncomeCap)
    return { deduction: Math.max(0, deduction), limited: true }
  } else {
    // Non-SSTB: phase in the W-2/UBIA limit
    const w2UbiaLimit = Math.max(w2Wages * 0.50, w2Wages * 0.25 + ubia * 0.025)
    const excessOverLimit = Math.max(0, twentyPercentQBI - w2UbiaLimit)
    const reductionAmount = excessOverLimit * phaseOutFraction
    const deduction = Math.min(twentyPercentQBI - reductionAmount, taxableIncomeCap)
    return { deduction: Math.max(0, deduction), limited: true }
  }
}

export function calculateSelfEmploymentTax(input: SelfEmploymentTaxInput) {
  const netSEIncome = Math.max(0, input.grossIncome - input.businessExpenses)
  const isSSTB = input.isSSTB || false
  const w2WagesPaidByBiz = input.w2WagesPaidByBiz || 0
  const ubiaQualifiedProperty = input.ubiaQualifiedProperty || 0

  // SE tax is calculated on 92.35% of net SE income
  const seTaxableIncome = netSEIncome * 0.9235

  // Social Security portion (up to wage base, considering W-2 income)
  const w2SSWages = Math.min(input.otherW2Income, FICA_CONSTANTS.ssWageBase)
  const remainingSSBase = Math.max(0, FICA_CONSTANTS.ssWageBase - w2SSWages)
  const ssTaxableAmount = Math.min(seTaxableIncome, remainingSSBase)
  const ssTax = ssTaxableAmount * FICA_CONSTANTS.ssRate * 2 // both halves

  // Medicare portion (no cap)
  const medicareTax = seTaxableIncome * FICA_CONSTANTS.medicareRate * 2

  // Additional Medicare Tax on combined income over threshold
  const totalIncome = input.otherW2Income + netSEIncome
  const additionalMedicareTax = totalIncome > FICA_CONSTANTS.additionalMedicareThreshold
    ? Math.max(0, seTaxableIncome - Math.max(0, FICA_CONSTANTS.additionalMedicareThreshold - input.otherW2Income)) * FICA_CONSTANTS.additionalMedicareRate
    : 0

  const totalSETax = ssTax + medicareTax + additionalMedicareTax

  // Deductible half of SE tax
  const seDeduction = totalSETax / 2

  // QBI deduction — compute taxable income before QBI first
  const standardDeduction = STANDARD_DEDUCTIONS[input.filingStatus] || STANDARD_DEDUCTIONS.single
  const combinedIncome = input.otherW2Income + netSEIncome
  const taxableIncomeBeforeQBI = Math.max(0, combinedIncome - standardDeduction - seDeduction)

  const qbiResult = calculateQBI(
    netSEIncome,
    taxableIncomeBeforeQBI,
    input.filingStatus,
    isSSTB,
    w2WagesPaidByBiz,
    ubiaQualifiedProperty,
  )
  const qbiDeduction = qbiResult.deduction

  // Federal income tax calculation
  const totalDeductions = standardDeduction + seDeduction + qbiDeduction
  const taxableIncome = Math.max(0, combinedIncome - totalDeductions)
  const federalIncomeTax = calculateTaxOnIncome(taxableIncome, input.filingStatus)

  // State tax
  const stateEntry = STATE_TAX_DATA.find(s => s.code === input.stateCode)
  const stateRate = stateEntry?.code === 'CUSTOM'
    ? (input.customStateTaxRate || 0)
    : (stateEntry?.rate || 0)
  const stateTax = taxableIncome * (stateRate / 100)

  // Total tax liability
  const totalTax = federalIncomeTax + totalSETax + stateTax

  // Quarterly estimated payments
  const quarterlyPayment = totalTax / 4

  // Effective and marginal rates
  const effectiveRate = combinedIncome > 0 ? (totalTax / combinedIncome) * 100 : 0
  const marginalRate = calculateMarginalRate(taxableIncome, input.filingStatus)

  // Take-home
  const takeHome = combinedIncome - totalTax

  return {
    grossIncome: input.grossIncome,
    businessExpenses: input.businessExpenses,
    netSEIncome,
    seTaxableIncome: Math.round(seTaxableIncome),
    ssTax: Math.round(ssTax),
    medicareTax: Math.round(medicareTax),
    additionalMedicareTax: Math.round(additionalMedicareTax),
    totalSETax: Math.round(totalSETax),
    seDeduction: Math.round(seDeduction),
    qbiDeduction: Math.round(qbiDeduction),
    qbiLimited: qbiResult.limited,
    federalIncomeTax: Math.round(federalIncomeTax),
    stateTax: Math.round(stateTax),
    stateRate,
    totalTax: Math.round(totalTax),
    quarterlyPayment: Math.round(quarterlyPayment),
    effectiveRate: Math.round(effectiveRate * 10) / 10,
    marginalRate,
    taxableIncome: Math.round(taxableIncome),
    totalDeductions: Math.round(totalDeductions),
    takeHome: Math.round(takeHome),
    combinedIncome: Math.round(combinedIncome),
  }
}
